import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import B2becPagination from '@/components/B2becPagination/B2becPagination'
import { useAppSelector } from '@/store/hook'
import { selectHideStatus } from '@/store/selectors/configSelector'
import useAdobeAnalysis from '@/hooks/useAdobeAnalysis'
import useBreadcrumbs from '@/hooks/useBreadcrumbs'
import useDocumentTitle from '@/hooks/useDocumentTitle'
import { PAGE_SIZE } from '@/libs/constants'
import { PAGE_TYPES } from '@/libs/constants/adobeAnalytics'

import { DatePeriod } from './DatePeriod'
import styles from './OrdersHistory.module.scss'
import OrdersHistoryTable from './OrdersHistoryTable'
import { Search } from './Search'
import { Status } from './Status'
import { useOrdersQuery } from './useOrdersQuery'

const OrdersHistoryPage = ({ breadcrumbs }) => {
  const hideStatusSelectOption = useAppSelector(selectHideStatus)
  useDocumentTitle('pageTitle.orderHistory')
  useBreadcrumbs(breadcrumbs)

  useAdobeAnalysis(null, {
    pageName: 'order history overview',
    pageType: PAGE_TYPES.ORDER,
    pageId: '5',
  })

  const { t } = useTranslation()

  const {
    items,
    totalCount,
    isPending,
    currentPage,
    fromDate,
    toDate,
    searchTerm,
    selectedStatus,
    handlePageChange,
    handleDateChange,
    handleSearch,
    handleChangeSorter,
    handleFilterByStatus,
  } = useOrdersQuery()

  const search = useMemo(() => {
    return <Search searchTerm={searchTerm} onSearch={handleSearch} />
  }, [searchTerm, handleSearch])

  const status = useMemo(() => {
    return (
      <Status
        selectedStatus={selectedStatus}
        onSelectStatus={handleFilterByStatus}
      />
    )
  }, [selectedStatus, handleFilterByStatus])

  const datePeriod = useMemo(() => {
    return <DatePeriod from={fromDate} to={toDate} onDateChange={handleDateChange} />
  }, [fromDate, toDate, handleDateChange])

  const orderHistoryTable = useMemo(() => {
    return (
      <OrdersHistoryTable
        currentPage={currentPage}
        isLoading={isPending}
        items={items}
        onChange={handleChangeSorter}
      />
    )
  }, [currentPage, isPending, items, handleChangeSorter])

  const pagination = useMemo(() => {
    return (
      <B2becPagination
        current={currentPage}
        pageSize={PAGE_SIZE[10]}
        total={totalCount}
        onPageChange={handlePageChange}
      />
    )
  }, [currentPage, totalCount, handlePageChange])

  return (
    <div className="container">
      <h1 className={styles.title}>{t('orders.history.title')}</h1>
      <div className={styles.header}>
        {search}
        <div className={styles.right}>
          {datePeriod}
          {!hideStatusSelectOption && status}
        </div>
      </div>
      {orderHistoryTable}
      {pagination}
    </div>
  )
}

export default OrdersHistoryPage
